import { createLocalStoragePersister } from '@client/utils/data/zustand';
import { safeLocalStorage } from '@client/utils/storage/safe-storage';
import superjson from 'superjson';
import { create } from 'zustand';
import { persist, type StorageValue } from 'zustand/middleware';
import type { AppState } from './types';

const APP_STATE_STORAGE_KEY = 'appState';

const storage = createLocalStoragePersister<AppState>();

export const useAppStateStore = create<AppState>()(
    persist<AppState>(
        (set, get) => ({
            isOnline: true,
            connectionState: {
                quality: 'good',
                lastChecked: null,
                consecutiveFailures: 0,
            },
            cancelUploadSignal: null,
            uploadCancelers: {},
            pendingUploads: [],
            appBuildNumber: '',
            appBuildLastUpdatedAt: null,
            versionSnoozeUntil: null,
            versionDismissedBuild: null,
            silentUpdatePending: false,
            silentUpdateDetectedAt: null,
            silentUpdateBuildNumber: null,
            silentUpdateEnabled: false,
            disableDebugInfo: false,
            claimLodgementSettings: {
                fieldRequiredMessage: 'Please complete this section before continuing the claim.',
            },
            drawer: {
                show: false,
            },
            isLeftNavCollapsed: false,
            fullPageElement: null,
            dismissedSystemAlertIds: [],
            actions: {
                setIsOnline: (isOnline) => {
                    set({ isOnline });
                    // Update connection quality based on online status
                    if (!isOnline) {
                        set((state) => ({
                            connectionState: {
                                ...state.connectionState,
                                quality: 'offline',
                                lastChecked: new Date(),
                            },
                        }));
                    } else {
                        // When coming back online, reset to good quality
                        set({
                            connectionState: {
                                quality: 'good',
                                lastChecked: new Date(),
                                consecutiveFailures: 0,
                            },
                        });
                    }
                },
                setConnectionQuality: (quality) =>
                    set((state) => ({
                        connectionState: {
                            ...state.connectionState,
                            quality,
                            lastChecked: new Date(),
                        },
                    })),
                recordConnectionFailure: () =>
                    set((state) => {
                        const newFailureCount = state.connectionState.consecutiveFailures + 1;
                        // After 3 consecutive failures, mark connection as poor
                        const newQuality = newFailureCount >= 3 ? 'poor' : state.connectionState.quality;

                        return {
                            connectionState: {
                                quality: newQuality,
                                lastChecked: new Date(),
                                consecutiveFailures: newFailureCount,
                            },
                        };
                    }),
                resetConnectionFailures: () =>
                    set({
                        connectionState: {
                            quality: 'good',
                            lastChecked: new Date(),
                            consecutiveFailures: 0,
                        },
                    }),
                addPendingUpload: (upload) =>
                    set((state) => ({
                        pendingUploads: [...state.pendingUploads, { ...upload, status: 'uploading', percent: 0 }],
                    })),

                removePendingUpload: (uid) =>
                    set((state) => ({
                        pendingUploads: state.pendingUploads.filter((upload) => upload.uid !== uid),
                    })),

                cancelCurrentUpload: () => {
                    const { uploadCancelers } = get();

                    Object.values(uploadCancelers).forEach((controller) => controller.abort());

                    set({ pendingUploads: [], uploadCancelers: {} });
                },

                registerUploadCanceler: (uid, controller) =>
                    set((state) => ({
                        uploadCancelers: {
                            ...state.uploadCancelers,
                            [uid]: controller,
                        },
                    })),

                clearUploadCanceler: (uid) =>
                    set((state) => {
                        if (!(uid in state.uploadCancelers)) {
                            return {};
                        }

                        const { [uid]: _removed, ...uploadCancelers } = state.uploadCancelers;

                        return { uploadCancelers };
                    }),

                updatePendingUploadProgress: (uid, percent) =>
                    set((state) => ({
                        pendingUploads: state.pendingUploads.map((upload) =>
                            upload.uid === uid ? { ...upload, percent } : upload,
                        ),
                    })),
                setAppBuildNumber: (version) => set({ appBuildNumber: version }),
                setAppBuildLastUpdatedAt: (lastUpdatedAt) => set({ appBuildLastUpdatedAt: lastUpdatedAt }),
                setVersionSnoozeUntil: (until) => set({ versionSnoozeUntil: until }),
                setVersionDismissedBuild: (buildNumber) => set({ versionDismissedBuild: buildNumber }),
                setSilentUpdatePending: (buildNumber) =>
                    set((state) => {
                        const isSameBuild = state.silentUpdatePending && state.silentUpdateBuildNumber === buildNumber;

                        return {
                            silentUpdatePending: true,
                            silentUpdateBuildNumber: buildNumber,
                            silentUpdateDetectedAt: isSameBuild ? state.silentUpdateDetectedAt : Date.now(),
                        };
                    }),
                clearSilentUpdate: () =>
                    set({
                        silentUpdatePending: false,
                        silentUpdateDetectedAt: null,
                        silentUpdateBuildNumber: null,
                    }),
                setSilentUpdateEnabled: (enabled) => set({ silentUpdateEnabled: enabled }),
                setDisableDebugInfo: (disableDebugInfo) => set({ disableDebugInfo }),
                setIsLeftNavCollapsed: (isCollapsed) => set({ isLeftNavCollapsed: isCollapsed }),
                setFullPageElement: (element) => set({ fullPageElement: element }),
                dismissSystemAlert: (id) =>
                    set((state) =>
                        state.dismissedSystemAlertIds.includes(id)
                            ? state
                            : { dismissedSystemAlertIds: [...state.dismissedSystemAlertIds, id] },
                    ),
            },
        }),
        {
            name: APP_STATE_STORAGE_KEY,
            storage,
            partialize: (state) =>
                Object.fromEntries(
                    Object.entries(state).filter(([key]) =>
                        [
                            'disableDebugInfo',
                            'isLeftNavCollapsed',
                            'appBuildNumber',
                            'appBuildLastUpdatedAt',
                            'versionSnoozeUntil',
                            'versionDismissedBuild',
                            'dismissedSystemAlertIds',
                        ].includes(key),
                    ),
                ) as AppState,
        },
    ),
);

/**
 * Cross-tab sync for the version dismissal.
 *
 * Zustand's `persist` middleware writes to localStorage but does not broadcast
 * changes to other open tabs. Without this, dismissing the "New version available"
 * toast in one tab would leave it showing in every other open tab until the user
 * interacts there. We mirror only `versionDismissedBuild` via the `storage` event,
 * which fires in *other* tabs (never the originating one — so there is no feedback
 * loop). The dismissed build flows into each tab's reactive state, the version-check
 * effect in `useAppBuildVersion` re-runs, sees the build as dismissed, and destroys
 * the open toast.
 *
 * Scoped deliberately to `versionDismissedBuild` — other persisted keys (nav
 * collapse, debug toggles) intentionally stay per-tab. The guard against the current
 * value makes the inevitable cross-tab write flurry self-terminate.
 *
 * Mirroring goes through the persisted setter, which re-writes this tab's *whole*
 * partialized snapshot. That snapshot can be staler than what the originating tab
 * just persisted for the other keys, so a naive mirror would clobber those newer
 * values. To prevent that, we restore the originating tab's canonical envelope
 * (`event.newValue`, the exact string it wrote) verbatim right after mirroring — the
 * reactive `versionDismissedBuild` update survives, while every other persisted key
 * is left exactly as the originating tab wrote it. Writing localStorage in this tab
 * does not re-fire `storage` here, so there is no loop.
 */
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (event.key !== APP_STATE_STORAGE_KEY || !event.newValue) {
            return;
        }

        let nextDismissedBuild: string | null;
        try {
            nextDismissedBuild =
                superjson.parse<StorageValue<AppState>>(event.newValue).state.versionDismissedBuild ?? null;
        } catch {
            return;
        }

        if (nextDismissedBuild !== useAppStateStore.getState().versionDismissedBuild) {
            useAppStateStore.getState().actions.setVersionDismissedBuild(nextDismissedBuild);
            // Restore the originating tab's canonical envelope so mirroring the dismissed
            // build into reactive state never overwrites its newer values for the other
            // persisted keys. See the block comment above.
            safeLocalStorage.setItem(APP_STATE_STORAGE_KEY, event.newValue);
        }
    });
}
